import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { Segment } from '@core/interfaces';
import { fuseAnimations } from '@fuse/animations';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, tap } from 'rxjs/operators';
import { DialogSegmentsComponent } from './dialogs/dialog-segments.component';
import { FiltersComponent } from './filters/filters.component';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { SegmentService } from './services/segment.service';

@Component({
  templateUrl: './segments.component.html',
  styleUrls: ['./segments.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None
})
export class SegmentComponent implements OnInit, OnDestroy {

  @ViewChild(MatPaginator, { static: true }) private paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) private sort: MatSort;
  @ViewChild(FiltersComponent, { static: true }) filterSegmentsComponent: FiltersComponent;
  @ViewChild('datatable', { read: ElementRef, static: true }) datatable: ElementRef<HTMLDivElement>;

  segmentDetailSidebarName = 'segmentDetail';
  displayedColumns = ['name', 'createdAt', 'edit'];
  segments: Segment[] = [];
  totalCount: Number;
  filtered = false;
  loading = true;

  private subscriptions = new Subject<void>();
  segmentsReload = new Subject<any>();

  public hasEditSegmentPermission = this.authenticationService.hasPermission('segments-edit');
  public hasCreateSegmentPermission = this.authenticationService.hasPermission('segments-create');

  constructor(
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private authenticationService: AuthenticationService,
    private progressBarService: FuseProgressBarService,
    private sidebarService: FuseSidebarService,
    private segmentService: SegmentService,
    private matDialog: MatDialog,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.sort.active = 'name';
    this.sort.direction = 'asc';

    merge(
      this.sort.sortChange,
      this.paginator.page,
      this.filterSegmentsComponent.filterChanged.pipe(
        tap(() => this.paginator.firstPage())
      ),
      this.segmentsReload
    )
      .pipe(debounceTime(500),
        takeUntil(this.subscriptions),
        switchMap(() => {
          this.sort.direction = this.sort.direction || 'asc';

          const options = {
            orderBy: `${this.sort.active}_${this.sort.direction}`,
            offset: this.paginator.pageIndex * this.paginator.pageSize,
            limit: this.paginator.pageSize
          };

          this.loading = true;
          return this.segmentService.loadData({ where: this.filterSegmentsComponent.filterChanged.value, options });
        }))
      .pipe(takeUntil(this.subscriptions))
      .subscribe((result) => {
        this.datatable.nativeElement.scrollTo({
          top: 0,
        });

        this.segments = result.items;
        this.totalCount = result.totalCount;
        this.filtered = result.filtered;
        this.loading = false;
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  reload(): void {
    this.segmentsReload.next();
  }

  toggleFilterSegmentsSidebar(): void {
    this.sidebarService.getSidebar('filterSegments').toggleOpen();
  }

  async openDialog(segmentId?: string): Promise<void> {
    this.progressBarService.show();

    let segment = null;
    if (segmentId) {
      segment = await this.segmentService.getSegmentById(segmentId);
    } else {
      segment = {
        archived: false,
        name: null,
        segmentMaintenanceTypeForms: []
      };
    }

    const segmentDialog = this.matDialog.open(DialogSegmentsComponent, {
      panelClass: ['dialog-segments'],
      data: {
        segment,
        isViewMode: segmentId && !this.hasEditSegmentPermission
      }
    });

    segmentDialog.afterClosed().subscribe((reload) => {
      if (reload) {
        this.reload();
      }
    });

    this.progressBarService.hide();
  }

  clearFilters(): void {
    this.filtered = false;
    this.filterSegmentsComponent.clearFilters();
  }
}
